import { Vec3, vec3, add, sub, scale, dot, length } from '../core/math/vec3'
import { rotate, unrotate } from '../core/math/quat'
import { CelestialBody } from '../core/world/body'
import { Vessel, partById, subtreeOf } from '../core/vessel/vessel'
import { PartCatalog, partDef } from '../core/vessel/catalog'
import { MassProperties, computeMassProperties } from '../core/vessel/massProperties'
import { vesselExtents } from '../core/vessel/extents'
import { RigidState } from '../core/dynamics/rigidBody'
import {
  FlightContext, FlightControls, stepFlight, altitudeOf, atmosphereVelocity,
  engineOutputs,
} from '../core/dynamics/flight'
import { pressureRatio, densityAt } from '../core/world/atmosphere'
import { cross } from '../core/math/vec3'
import { localUp } from '../core/nav/attitude'
import { soiCorrente, bodyPositionRelative, bodyVelocityRelative } from '../core/orbit/ephemeris'
import { mulVec3, inverse } from '../core/math/mat3'
import { aeroForces } from '../core/aero/forces'
import {
  Giunto, Flessione, flessioneNulla, giuntiDi, momentiFlettenti,
  aggiornaFlessione, giuntoRotto, utilizzoStrutturale, applicaFlessione, ForzaSuParte,
} from '../core/structure/bending'
import { StatoTermico, statoTermicoIniziale, passoTermico } from '../core/thermal/heating'
import { Paracadute, paracaduteDi, aggiornaParacadute, forzeParacadute } from '../core/aero/parachute'
import { puntiDiContatto, forzeDiContatto } from '../core/contact/ground'
import { dynamicPressure, surfaceVelocity } from '../core/nav/readouts'
import { separaAlGiunto } from './rottura'
import { LaunchSite, launchState } from './launch'
import { stagesOf, separate } from './staging'
import { OnRails, suRotaia, statoDaRotaia, avanzaSuRotaia, cambioSfera, puoStareSuRotaia } from './rails'

export interface SimVessel {
  readonly id: string
  vessel: Vessel
  state: RigidState
  massProps: MassProperties
  activeEngines: string[]
  controls: FlightControls
  /** true per gli stadi sganciati: non si pilotano e prima o poi spariscono */
  readonly debris: boolean
  landed: boolean
  /** quando e su rotaia non viene integrato ma propagato con Keplero */
  rails: OnRails | null
  /** deformazione dei giunti strutturali */
  flessione: Flessione
  /** quanto il giunto piu sollecitato e vicino a rompersi, 0..oltre 1 */
  caricoStrutturale: number
  termico: StatoTermico
  paracadute: Paracadute[]
  /** true quando il pilota ha chiesto l'apertura */
  paracaduteAperti: boolean
}

export interface Simulation {
  /** corpo di riferimento: cambia attraversando una sfera d'influenza */
  body: CelestialBody
  readonly catalog: PartCatalog
  time: number
  vessels: SimVessel[]
  activeId: string
  /** prossimo stadio da attivare; si parte da 0 */
  nextStage: number
  /** moltiplicatore del tempo: fino a 4 si integra, oltre si va su rotaia */
  timeWarp: number
  /** true quando il veicolo attivo si e disintegrato del tutto */
  perso: boolean
}

/** I fattori di accelerazione: i primi tre integrano, gli altri vanno su rotaia. */
export const FATTORI_TEMPO = [1, 2, 4, 10, 50, 100, 1000, 10_000, 100_000] as const
export const MASSIMO_FISICO = 4

const SPENTO: FlightControls = { throttle: 0, gimbal: { pitch: 0, yaw: 0 } }

let contatore = 0
const nuovoId = (prefisso: string): string => `${prefisso}-${++contatore}`

export function createSimulation(
  catalog: PartCatalog, body: CelestialBody, site: LaunchSite, vessel: Vessel,
): Simulation {
  const id = nuovoId('veicolo')
  const sv: SimVessel = {
    id,
    vessel,
    state: launchState(body, site, vessel, catalog),
    massProps: computeMassProperties(vessel, catalog),
    activeEngines: [],
    controls: { ...SPENTO },
    debris: false,
    landed: true,
    rails: null,
    flessione: flessioneNulla(),
    caricoStrutturale: 0,
    termico: statoTermicoIniziale(vessel, catalog),
    paracadute: paracaduteDi(vessel, catalog),
    paracaduteAperti: false,
  }
  return { body, catalog, time: 0, vessels: [sv], activeId: id, nextStage: 0, timeWarp: 1, perso: false }
}

export function activeVessel(sim: Simulation): SimVessel {
  const sv = sim.vessels.find((v) => v.id === sim.activeId)
  if (!sv) throw new Error('Nessun veicolo attivo nella simulazione')
  return sv
}

/** Posizione mondo di una parte, tenendo conto che lo stato segue il baricentro. */
export function partWorldPosition(sv: SimVessel, partId: string, _catalog: PartCatalog): Vec3 {
  const p = partById(sv.vessel, partId)
  return add(sv.state.position, rotate(sv.state.orientation, sub(p.position, sv.massProps.com)))
}

/** Punto piu basso lungo l'asse del veicolo: quello che tocca la rampa. */
export function bottomWorldPosition(sv: SimVessel, catalog: PartCatalog): Vec3 {
  const ext = vesselExtents(sv.vessel, catalog)
  return add(
    sv.state.position,
    rotate(sv.state.orientation, sub(vec3(0, ext.minY, 0), sv.massProps.com)),
  )
}

/**
 * Ricalcola le proprieta di massa e CORREGGE la posizione.
 *
 * `RigidState.position` e la posizione del CENTRO DI MASSA, perche e cosi che
 * sono scritte le equazioni in core/dynamics. Ma il centro di massa migra
 * dentro il veicolo mentre il propellente cala: senza correzione, il razzo
 * trasla di due metri senza che nessuna forza lo abbia spinto.
 */
export function refreshMassProperties(sv: SimVessel, catalog: PartCatalog): void {
  const nuovo = computeMassProperties(sv.vessel, catalog)
  const spostamento = sub(nuovo.com, sv.massProps.com)
  sv.state = {
    ...sv.state,
    position: add(sv.state.position, rotate(sv.state.orientation, spostamento)),
  }
  sv.massProps = nuovo
}

const omegaPianeta = (body: CelestialBody): Vec3 =>
  body.rotationPeriod > 0 ? vec3(0, 0, (2 * Math.PI) / body.rotationPeriod) : vec3(0, 0, 0)

/**
 * Contatto col suolo, versione F2: il veicolo appoggia e si ferma.
 * Non ci sono gambe, attrito ne ribaltamento — quelli sono F5. Qui serve solo
 * che il razzo stia sulla rampa invece di sprofondare, e che possa
 * staccarsene appena la spinta supera il peso.
 */
export function clampToGround(sv: SimVessel, body: CelestialBody, catalog: PartCatalog): void {
  const profondita = -altitudeOf(bottomWorldPosition(sv, catalog), body)
  if (profondita <= 0) { sv.landed = false; return }

  const up = localUp(sv.state.position)
  const suolo = atmosphereVelocity(sv.state.position, body)
  const verticaleRelativa = dot(sub(sv.state.velocity, suolo), up)

  sv.state = {
    position: add(sv.state.position, scale(up, profondita)),
    velocity: verticaleRelativa < 0 ? suolo : sv.state.velocity,
    orientation: sv.state.orientation,
    angularVelocity: verticaleRelativa < 0
      ? unrotate(sv.state.orientation, omegaPianeta(body))
      : sv.state.angularVelocity,
  }
  sv.landed = true
}

/**
 * Cambia il corpo di riferimento, riscrivendo lo stato di tutti i veicoli.
 * Posizione e velocita ASSOLUTE non cambiano: cambia solo il sistema in cui
 * le si racconta.
 */
export function cambiaRiferimento(sim: Simulation, nuovo: CelestialBody): void {
  if (nuovo.id === sim.body.id) return
  const dp = bodyPositionRelative(sim.body.id, nuovo.id, sim.time)
  const dv = bodyVelocityRelative(sim.body.id, nuovo.id, sim.time)

  for (const sv of sim.vessels) {
    sv.state = {
      ...sv.state,
      position: add(sv.state.position, dp),
      velocity: add(sv.state.velocity, dv),
    }
    if (sv.rails) sv.rails = suRotaia(sv.state, nuovo, sim.time)
  }
  sim.body = nuovo
}

/**
 * Cambia il fattore di accelerazione del tempo.
 *
 * Oltre 4× si passa su rotaia, e per farlo il veicolo attivo deve poterci
 * stare: motori spenti, fuori dall'atmosfera, non a terra. Se non puo, il
 * fattore non cambia e la funzione lo dice, cosi l'interfaccia puo spiegare
 * perche invece di ignorare il comando in silenzio.
 */
export function impostaTimeWarp(sim: Simulation, fattore: number): boolean {
  if (!FATTORI_TEMPO.includes(fattore as typeof FATTORI_TEMPO[number])) return false

  if (fattore > MASSIMO_FISICO) {
    const attivo = activeVessel(sim)
    if (!puoStareSuRotaia(attivo, sim.body)) return false
    for (const sv of sim.vessels) {
      if (!sv.rails && puoStareSuRotaia(sv, sim.body)) sv.rails = suRotaia(sv.state, sim.body, sim.time)
    }
  } else {
    for (const sv of sim.vessels) {
      if (sv.rails) { sv.state = statoDaRotaia(sv.rails, sv.state); sv.rails = null }
    }
  }

  sim.timeWarp = fattore
  return true
}

/** Toglie la rotaia a chi non puo piu starci: succede quando si accende un motore. */
function verificaRotaie(sim: Simulation): void {
  for (const sv of sim.vessels) {
    if (sv.rails && !puoStareSuRotaia(sv, sim.body)) {
      sv.state = statoDaRotaia(sv.rails, sv.state)
      sv.rails = null
      if (sim.timeWarp > MASSIMO_FISICO) sim.timeWarp = MASSIMO_FISICO
    }
  }
}

export function stepSimulation(sim: Simulation, dt: number): void {
  if (sim.perso || sim.vessels.length === 0) { sim.time += dt; return }
  verificaRotaie(sim)

  // ── chi e su rotaia non si integra: si propaga con Keplero, e il costo non
  //    cresce col fattore di accelerazione del tempo
  for (const sv of sim.vessels) {
    if (!sv.rails) continue
    sv.rails = avanzaSuRotaia(sv.rails, dt)
    sv.state = statoDaRotaia(sv.rails, sv.state)
  }

  // ── chi non ci sta viene integrato, come sempre
  const rotti: Array<{ sv: SimVessel; giunto: string }> = []
  const bruciate: Array<{ sv: SimVessel; ids: string[] }> = []

  for (const sv of sim.vessels) {
    if (sv.rails) continue

    const contatti = puntiDiContatto(sv.vessel, sim.catalog)
    const ctx: FlightContext = {
      vessel: sv.vessel,
      catalog: sim.catalog,
      body: sim.body,
      controls: sv.debris ? SPENTO : sv.controls,
      activeEngines: sv.debris ? [] : sv.activeEngines,
      contatti,
      paracadute: sv.paracadute,
    }
    const r = stepFlight(ctx, sv.state, sv.massProps, sim.time, dt)
    sv.state = r.state
    if (r.propellantUsed > 0) refreshMassProperties(sv, sim.catalog)

    // le gambe fanno il loro mestiere: il vincolo rigido serve solo a chi non ne ha
    if (contatti.length === 0) clampToGround(sv, sim.body, sim.catalog)
    else sv.landed = altitudeOf(bottomWorldPosition(sv, sim.catalog), sim.body) < 0.5

    const quota = altitudeOf(sv.state.position, sim.body)
    const densita = densityAt(sim.body.atmosphere, quota)
    const ariaCorpo = densita > 0
      ? unrotate(sv.state.orientation, scale(surfaceVelocity(sv.state, sim.body), -1))
      : vec3(0, 0, 0)

    // ── struttura: momenti flettenti, deformazione, e rottura
    const giunti = giuntiDi(sv.vessel, sim.catalog)
    if (giunti.length > 0) {
      // Tutte le forze esterne applicate parte per parte: aria, spinta,
      // contatto. La GRAVITA resta fuori di proposito da entrambi i lati del
      // conto — agisce su ogni parte in proporzione alla sua massa, quindi
      // nel termine (F − m·a) si cancella esattamente. Metterla in uno solo
      // dei due lati inventerebbe un momento che non esiste.
      const forze: ForzaSuParte[] = densita > 0
        ? aeroForces(sv.vessel, sim.catalog, ariaCorpo, densita)
          .map((f) => ({ partId: f.partId, force: f.force, point: f.point }))
        : []

      const pRatio = pressureRatio(sim.body.atmosphere, quota)
      const motori = engineOutputs(ctx, pRatio)
      for (const e of motori) {
        if (e.thrust > 0) forze.push({ partId: e.partId, force: scale(e.direction, e.thrust), point: e.point })
      }
      for (const c of forzeDiContatto(contatti, sv.state, sv.massProps, sim.body)) {
        forze.push({ partId: c.partId, force: c.force, point: c.point })
      }

      // accelerazione del corpo dovuta a QUELLE forze, non alla gravita
      let risultante = vec3(0, 0, 0)
      let coppia = vec3(0, 0, 0)
      for (const f of forze) {
        risultante = add(risultante, f.force)
        coppia = add(coppia, cross(sub(f.point, sv.massProps.com), f.force))
      }
      const invI = inverse(sv.massProps.inertia)
      const accelerazione = scale(risultante, 1 / sv.massProps.mass)
      const alfa = mulVec3(invI, sub(coppia,
        cross(sv.state.angularVelocity, mulVec3(sv.massProps.inertia, sv.state.angularVelocity))))

      const momenti = momentiFlettenti(
        sv.vessel, sim.catalog, forze, sv.massProps,
        accelerazione, alfa, sv.state.angularVelocity, giunti,
      )
      sv.flessione = aggiornaFlessione(sv.flessione, momenti, giunti, dt)
      sv.caricoStrutturale = utilizzoStrutturale(momenti, giunti)
      const cede = giuntoRotto(momenti, giunti)
      if (cede && !sv.debris) rotti.push({ sv, giunto: cede })
    }

    // ── calore, e quello che il calore si porta via
    if (densita > 0) {
      const t = passoTermico(
        sv.termico, sv.vessel, sim.catalog, sim.body, quota, ariaCorpo, sv.state.orientation, dt,
      )
      sv.termico = t.stato
      if (t.distrutte.length > 0) bruciate.push({ sv, ids: t.distrutte })
    }

    // ── paracadute
    if (sv.paracadute.length > 0) {
      sv.paracadute = aggiornaParacadute(
        sv.paracadute, sv.vessel, sim.catalog, quota,
        dynamicPressure(sv.state, sim.body), sv.paracaduteAperti,
      )
    }
  }

  for (const { sv, ids } of bruciate) disintegra(sim, sv, ids)
  // se il veicolo attivo e sparito non c'e piu niente da aggiornare, e
  // soprattutto non c'e piu nessuno a cui chiedere dove siamo
  if (sim.perso) { sim.time += dt; return }
  for (const { sv, giunto } of rotti) if (sv.vessel.parts.length > 0) spezza(sim, sv, giunto)

  sim.time += dt

  // ── passaggio di sfera d'influenza, deciso dal veicolo attivo
  const attivo = activeVessel(sim)
  const nuovoCorpo = soiCorrente(attivo.state.position, sim.body, sim.time)
  if (nuovoCorpo.id !== sim.body.id) cambiaRiferimento(sim, nuovoCorpo)

  // ── i detriti a terra o troppo lontani smettono di interessare
  const riferimento = activeVessel(sim)
  sim.vessels = sim.vessels.filter((sv) =>
    !sv.debris || (!sv.landed && length(sub(sv.state.position, riferimento.state.position)) < 100_000))
}

/**
 * Il calore si porta via delle parti. Quello che era appeso a loro se ne va
 * con loro: un pezzo che restasse a fluttuare dopo che il suo supporto e
 * evaporato non avrebbe senso.
 */
function disintegra(sim: Simulation, sv: SimVessel, ids: readonly string[]): void {
  const daTogliere = new Set<string>()
  for (const id of ids) {
    if (!sv.vessel.parts.some((p) => p.id === id)) continue
    for (const p of subtreeOf(sv.vessel, id)) daTogliere.add(p.id)
  }
  if (daTogliere.size === 0) return

  const rimaste = sv.vessel.parts.filter((p) => !daTogliere.has(p.id))
  if (rimaste.length === 0) {
    sim.vessels = sim.vessels.filter((v) => v.id !== sv.id)
    if (sv.id === sim.activeId) sim.perso = true
    return
  }

  const comVecchio = sv.massProps.com
  const radice = rimaste.some((p) => p.id === sv.vessel.rootId) ? sv.vessel.rootId : rimaste[0]!.id
  sv.vessel = {
    rootId: radice,
    parts: rimaste.map((p) => (p.id === radice ? { ...p, parentId: null } : p)),
  }
  const mp = computeMassProperties(sv.vessel, sim.catalog)
  sv.state = {
    ...sv.state,
    position: add(sv.state.position, rotate(sv.state.orientation, sub(mp.com, comVecchio))),
  }
  sv.massProps = mp
  sv.activeEngines = sv.activeEngines.filter((id) => !daTogliere.has(id))
  sv.paracadute = sv.paracadute.filter((x) => !daTogliere.has(x.partId))
  sv.flessione = flessioneNulla()
}

/**
 * Il veicolo si spezza a un giunto: quello che sta a valle diventa un relitto.
 * Non c'e nessun impulso di separazione, perche non e una separazione: e un
 * cedimento.
 */
function spezza(sim: Simulation, sv: SimVessel, giunto: string): void {
  const r = separaAlGiunto(sv.vessel, giunto)
  if (!r || r.detached.parts.length === 0) return

  const comVecchio = sv.massProps.com
  sv.vessel = r.remaining
  const mpRimasto = computeMassProperties(r.remaining, sim.catalog)
  sv.state = {
    ...sv.state,
    position: add(sv.state.position, rotate(sv.state.orientation, sub(mpRimasto.com, comVecchio))),
  }
  sv.massProps = mpRimasto
  sv.activeEngines = sv.activeEngines.filter((id) => r.remaining.parts.some((p) => p.id === id))
  sv.flessione = flessioneNulla()

  const mp = computeMassProperties(r.detached, sim.catalog)
  sim.vessels.push({
    id: nuovoId('relitto'),
    vessel: r.detached,
    state: {
      position: add(sv.state.position, rotate(sv.state.orientation, sub(mp.com, mpRimasto.com))),
      velocity: sv.state.velocity,
      orientation: sv.state.orientation,
      angularVelocity: sv.state.angularVelocity,
    },
    massProps: mp,
    activeEngines: [],
    controls: { ...SPENTO },
    debris: true,
    landed: false,
    rails: null,
    flessione: flessioneNulla(),
    caricoStrutturale: 0,
    termico: statoTermicoIniziale(r.detached, sim.catalog),
    paracadute: paracaduteDi(r.detached, sim.catalog),
    paracaduteAperti: false,
  })
}

/**
 * Attiva lo stadio successivo: prima sgancia, poi accende.
 * Restituisce false quando gli stadi sono finiti.
 */
export function stageUp(sim: Simulation): boolean {
  const sv = activeVessel(sim)
  const info = stagesOf(sv.vessel, sim.catalog).find((s) => s.stage === sim.nextStage)
  if (!info) return false

  for (const decId of info.decouplers) {
    if (!sv.vessel.parts.some((p) => p.id === decId)) continue

    const comVecchio = sv.massProps.com
    const impulso = partDef(sim.catalog, partById(sv.vessel, decId).defId).decoupler!.ejectionForce * 0.15
    const { remaining, detached } = separate(sv.vessel, sim.catalog, decId)

    sv.vessel = remaining
    const mpRimasto = computeMassProperties(remaining, sim.catalog)
    sv.state = {
      ...sv.state,
      position: add(sv.state.position, rotate(sv.state.orientation, sub(mpRimasto.com, comVecchio))),
    }
    sv.massProps = mpRimasto
    sv.activeEngines = sv.activeEngines.filter((id) => remaining.parts.some((p) => p.id === id))

    for (const pezzo of detached) {
      const mp = computeMassProperties(pezzo, sim.catalog)
      sim.vessels.push({
        id: nuovoId('detrito'),
        vessel: pezzo,
        state: {
          // stesso riferimento locale: basta spostarsi sul baricentro del pezzo
          position: add(sv.state.position, rotate(sv.state.orientation, sub(mp.com, mpRimasto.com))),
          velocity: add(sv.state.velocity, rotate(sv.state.orientation, vec3(0, -impulso / mp.mass, 0))),
          orientation: sv.state.orientation,
          angularVelocity: sv.state.angularVelocity,
        },
        massProps: mp,
        activeEngines: [],
        controls: { ...SPENTO },
        debris: true,
        landed: false,
        rails: null,
        flessione: flessioneNulla(),
        caricoStrutturale: 0,
        termico: statoTermicoIniziale(pezzo, sim.catalog),
        paracadute: paracaduteDi(pezzo, sim.catalog),
        paracaduteAperti: false,
      })
      // reazione uguale e contraria: la quantita di moto totale non cambia
      sv.state = {
        ...sv.state,
        velocity: add(sv.state.velocity, rotate(sv.state.orientation, vec3(0, impulso / sv.massProps.mass, 0))),
      }
    }
  }

  for (const engId of info.engines) {
    if (sv.vessel.parts.some((p) => p.id === engId) && !sv.activeEngines.includes(engId)) {
      sv.activeEngines.push(engId)
    }
  }

  sim.nextStage++
  return true
}
